import { useMemo, useState } from 'react'
import { graphql, useLazyLoadQuery } from 'react-relay'
import { ResponsiveContainer, Tooltip, Treemap } from 'recharts'
import { Box, Dialog, DialogContent, DialogTitle, Grid, IconButton } from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import { EmptyState } from '../../../../components/EmptyState/EmptyState'
import { getDollarFormat, serializeInsightRate } from '../../../../display'
import { aiDateRanges } from '../../../../pages/Insights'
import { useGlobalState } from '../../../../state/v2/store'
import { useInsightsFilterMenuParams } from '../FilterMenu/useFilterMenuParams'
import { CurrencyType } from '../TopPerformingAds/__generated__/TopPerformingAdsQuery.graphql'
import { KPI, PrimaryColorTreemapQuery } from './__generated__/PrimaryColorTreemapQuery.graphql'
import { KPI as PrismaKPI } from '@prisma/client'

type ColorTreemapNode = {
  name: string
  displayName: string
  fill: string
  textColor: string
  lift: number | null
  liftLabel: string
  kpiPresent: number | null
  kpiLabel: string
  spend: number | null
  spendLabel: string
  adsCount: number
  size: number
  contentUrls: string[]
}

const MAX_THUMBNAILS = 6

const COLOR_SWATCHES: Record<string, string> = {
  black: '#222222',
  blue: '#3868C8',
  brown: '#8B5A2B',
  coral: '#FF7F50',
  cyan: '#00BCD4',
  gold: '#D4A017',
  gray: '#6F6F6F',
  green: '#32CD32',
  lime: '#A3E635',
  magenta: '#C026D3',
  navy: '#1F3A68',
  orange: '#F28C28',
  pink: '#F48FB1',
  purple: '#8E4A92',
  red: '#D80000',
  teal: '#008080',
  white: '#F7F7F7',
  yellow: '#F6D743',
}

const FALLBACK_COLORS = [
  '#3868C8',
  '#32CD32',
  '#8E4A92',
  '#6F6F6F',
  '#70B4F2',
  '#D80000',
  '#F28C28',
  '#008080',
]

const normalizeColorName = (name: string) =>
  name.trim().toLowerCase().replace(/[_-]+/g, ' ').replace(/\s+/g, ' ')

const toTitleCase = (value: string) =>
  normalizeColorName(value)
    .split(' ')
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ')

const colorForName = (name: string) => {
  const normalized = normalizeColorName(name)
  if (COLOR_SWATCHES[normalized]) return COLOR_SWATCHES[normalized]

  const matchedKey = Object.keys(COLOR_SWATCHES).find((key) => normalized.includes(key))
  if (matchedKey) return COLOR_SWATCHES[matchedKey]

  const hash = normalized.split('').reduce((sum, char) => sum + char.charCodeAt(0), 0)
  return FALLBACK_COLORS[hash % FALLBACK_COLORS.length]
}

const textColorForBackground = (hex: string) => {
  const raw = hex.replace('#', '')
  const r = parseInt(raw.slice(0, 2), 16)
  const g = parseInt(raw.slice(2, 4), 16)
  const b = parseInt(raw.slice(4, 6), 16)
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255
  return luminance > 0.58 ? '#111827' : '#FFFFFF'
}

const formatLift = (lift: number | null) => {
  if (lift == null) return '-'
  const rounded = Math.round(lift * 100)
  return `${rounded > 0 ? '+' : ''}${rounded}%`
}

const truncateLabel = (label: string, width: number) => {
  const maxChars = Math.max(4, Math.floor(width / 8))
  if (label.length <= maxChars) return label
  return `${label.slice(0, Math.max(1, maxChars - 3))}...`
}

const PrimaryColorTreemapContent = (props: any) => {
  const { adPreviewEnabled, onSelect, ...rest } = props
  const node = { ...rest, ...(rest.payload ?? {}) } as Partial<ColorTreemapNode> & {
    x: number
    y: number
    width: number
    height: number
  }

  if (node.width <= 0 || node.height <= 0) return null

  const displayName = node.displayName || node.name || ''
  const fill = node.fill || '#6F6F6F'
  const textColor = node.textColor || textColorForBackground(fill)
  const showText = node.width >= 54 && node.height >= 38
  const showLift = node.width >= 70 && node.height >= 56

  const handleSelect =
    adPreviewEnabled && onSelect ? () => onSelect(node as ColorTreemapNode) : undefined

  return (
    <g style={adPreviewEnabled ? { cursor: 'pointer' } : undefined} onClick={handleSelect}>
      <rect
        x={node.x}
        y={node.y}
        width={node.width}
        height={node.height}
        fill={fill}
        stroke="#FFFFFF"
        strokeWidth={2}
      />
      {showText && (
        <>
          <text
            x={node.x + node.width / 2}
            y={node.y + node.height / 2 - (showLift ? 7 : 0)}
            textAnchor="middle"
            fill={textColor}
            fontSize={Math.max(11, Math.min(16, node.width / 8))}
            fontWeight={700}
          >
            {truncateLabel(displayName, node.width - 8)}
          </text>
          {showLift && (
            <text
              x={node.x + node.width / 2}
              y={node.y + node.height / 2 + 12}
              textAnchor="middle"
              fill={textColor}
              fontSize={12}
              fontWeight={700}
            >
              {node.liftLabel}
            </text>
          )}
        </>
      )}
    </g>
  )
}

const PrimaryColorTooltip = ({ active, payload, adPreviewEnabled }: any) => {
  const node = payload?.[0]?.payload as ColorTreemapNode | undefined
  if (!active || !node) return null

  return (
    <div className="rounded-md border border-gray-200 bg-white px-3 py-2 text-sm shadow-sm">
      <div className="font-semibold text-gray-900">{node.displayName}</div>
      <div className="text-gray-700">Label lift: {node.liftLabel}</div>
      <div className="text-gray-700">Spend: {node.spendLabel}</div>
      <div className="text-gray-700">KPI: {node.kpiLabel}</div>
      <div className="text-gray-700">Ads analyzed: {node.adsCount.toLocaleString()}</div>
      {adPreviewEnabled && (
        <div className="mt-2 text-center text-base italic text-gray-500">Click tile to view ads</div>
      )}
    </div>
  )
}

const isImageUrl = (url: string) => url.includes('/img')

const AdThumbnailsModal = ({
  node,
  onClose,
}: {
  node: ColorTreemapNode | null
  onClose: () => void
}) => {
  const thumbnails = useMemo(
    () => (node?.contentUrls?.filter((url) => !!url) ?? []).slice(0, MAX_THUMBNAILS),
    [node]
  )

  return (
    <Dialog open={!!node} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ pr: 6 }}>
        {node ? `${node.displayName} ads` : 'Ads'}
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{ position: 'absolute', right: 8, top: 8, color: (theme) => theme.palette.grey[500] }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        {thumbnails.length === 0 ? (
          <EmptyState message="No ad thumbnails available for this color." />
        ) : (
          <Grid container spacing={1}>
            {thumbnails.map((src) => (
              <Grid item xs={6} sm={4} key={src}>
                {isImageUrl(src) ? (
                  <Box sx={{ width: '100%' }}>
                    <img className="bg-gray-300 object-cover" src={src} alt="" style={{ width: '100%' }} />
                  </Box>
                ) : (
                  <Box sx={{ width: '100%' }}>
                    <video className="bg-gray-300 object-cover" controls style={{ width: '100%' }}>
                      <source src={src} />
                    </video>
                  </Box>
                )}
              </Grid>
            ))}
          </Grid>
        )}
      </DialogContent>
    </Dialog>
  )
}

export const PrimaryColorTreemap = ({ adPreviewEnabled = false }: { adPreviewEnabled?: boolean }) => {
  const [{ insightKpi, aiDateRange }] = useInsightsFilterMenuParams()
  const brandId = useGlobalState((b) => b.brand?.map((brand) => brand.id).join(','))
  const currencyType: CurrencyType = 'USD'
  const [selectedNode, setSelectedNode] = useState<ColorTreemapNode | null>(null)

  const variables = useMemo<PrimaryColorTreemapQuery['variables']>(() => ({
    skip: !brandId,
    input: {
      brandId: brandId!,
      kpi: insightKpi as KPI,
      aiDateRange: aiDateRange || aiDateRanges[0].value,
      evaluationType: 'TOP_PERFORMING',
    },
  }), [aiDateRange, insightKpi, brandId])

  const data = useLazyLoadQuery<PrimaryColorTreemapQuery>(
    graphql`
      query PrimaryColorTreemapQuery($input: GetAISummaryRequest!, $skip: Boolean!) {
        getPrimaryColorLabelLift(input: $input) @skip(if: $skip) {
          labelName
          lift
          kpiPresent
          spend
          adsCount
          contentUrls
        }
      }
    `,
    variables,
    { fetchPolicy: 'network-only' }
  )

  const chartData = useMemo<ColorTreemapNode[]>(() => {
    const colors = data.getPrimaryColorLabelLift || []
    const spendFormat = getDollarFormat(currencyType)
    return colors.map((color) => {
      const fill = colorForName(color.labelName)
      const lift = color.lift ?? null
      const kpiPresent = color.kpiPresent ?? null
      const spend = color.spend ?? null
      return {
        name: color.labelName,
        displayName: toTitleCase(color.labelName),
        fill,
        textColor: textColorForBackground(fill),
        lift,
        liftLabel: formatLift(lift),
        kpiPresent,
        kpiLabel: kpiPresent != null
          ? serializeInsightRate(kpiPresent, insightKpi as PrismaKPI, currencyType)
          : '-',
        spend,
        spendLabel: spend != null ? spendFormat.format(spend) : '-',
        adsCount: color.adsCount,
        size: Math.max(spend ?? 0, 0.01),
        contentUrls: (color.contentUrls ?? []).filter((url): url is string => !!url),
      }
    })
  }, [currencyType, data, insightKpi])

  if (chartData.length === 0) {
    return <EmptyState className="min-h-[320px]" message="No primary color lift data available for your filters." />
  }

  return (
    <div>
      <h5 className="text-2xl">Primary Color Treemap</h5>
      <p className="mb-4 text-sm text-gray-600">
        Shows every analyzed primary color. Tile size reflects spend magnitude.
      </p>
      <ResponsiveContainer width="100%" height={360}>
        <Treemap
          data={chartData}
          dataKey="size"
          nameKey="displayName"
          isAnimationActive={false}
          content={
            <PrimaryColorTreemapContent
              adPreviewEnabled={adPreviewEnabled}
              onSelect={setSelectedNode}
            />
          }
        >
          <Tooltip content={<PrimaryColorTooltip adPreviewEnabled={adPreviewEnabled} />} />
        </Treemap>
      </ResponsiveContainer>
      {adPreviewEnabled && (
        <AdThumbnailsModal node={selectedNode} onClose={() => setSelectedNode(null)} />
      )}
    </div>
  )
}

export default PrimaryColorTreemap
